import React, { useCallback, useMemo, useState } from "react";

/**
 * H10ModulePanel — guided H-10 setup inside the Database Manager H-10 Init tab.
 *
 * Flow: browser → same-origin house proxy (api/db_manager.php, validates session
 * + scoped bearer) → src new_customer_console.php → init_h10_module.php engine.
 * Bearer lives in sessionStorage("og_access_token"); never displayed/hardcoded.
 *
 * Sections:
 *   1. Module Status   — Init Dry Run / Init Repair / Readiness + section badges
 *   2. Missing Setup   — readiness missing items grouped by section
 *   3. Candidate Setup — classified workflow: Needs Fields / Ready / Excluded /
 *                        Already Configured, per-lease "Complete Setup" editor,
 *                        exclude/restore, and Execute selected → InjDispWellInfo
 *   4. Tenant Message  — generated request + Copy
 *
 * Drafts live in H10InjWellCandidateTemp; final InjDispWellInfo rows are written
 * only by Execute after server-side validation. No regulatory value is ever
 * defaulted into a confirmed write — completion is always explicit.
 */

const API_BASE = "/api/db_manager.php";

const SECTION_LABEL = {
  schema: "Schema / Module",
  tenant_config: "Tenant Config",
  regulatory_permit_config: "Regulatory Permit Config",
  operational_data: "Operational Data",
  cycle: "H-10 Cycle",
};
const LEVEL_COLOR = { FAIL: "#c0392b", WARN: "#b9770e", PASS: "#1e8449", INFO: "#566573" };
const OWNER_COLOR = { support: "#2563eb", tenant: "#b9770e", operator: "#b9770e", system: "#7c3aed", unknown: "#566573" };
const CAT_COLOR = { ready: "#1e8449", needs_fields: "#b9770e", excluded: "#7c3aed", already_configured: "#566573" };

// Fluid-% fields that must total 100 (mirrors server h10_fluid_sum_keys()).
const FLUID_KEYS = ["SWPct","NORMPct","FreshPct","TotalCO2Pct","NGPct","H2SPct","PolymerPct","SteamPct","AirPct","NitrogenPct","FracWaterFBPct","OtherPct"];
// Extra fluid keys (besides SW%) exposed in the modal's advanced section.
const EXTRA_FLUID_KEYS = ["NORMPct","FreshPct","TotalCO2Pct","AnthropogenicCO2Pct","NGPct","H2SPct","PolymerPct","SteamPct","AirPct","NitrogenPct","FracWaterFBPct","OtherPct"];

// Required regulatory fields, edited one lease at a time in the Complete Setup modal.
const MODAL_FIELDS = [
  { k: "WellNumber", label: "Well Number", kind: "text", help: "Well number / completion identifier on the lease." },
  { k: "H10MonthDue", label: "H-10 Due Month (MM)", kind: "text", ph: "09", help: "Annual H-10 due month, 01–12." },
  { k: "H10API", label: "API # (8 digits)", kind: "text", ph: "42xxxxxx", help: "8-digit Texas RRC API number." },
  { k: "UICType", label: "UIC Type", kind: "select", opts: [["1", "1"], ["2", "2"], ["3", "3 — salt-water disposal"], ["4", "4"], ["6", "6"]], help: "UIC well class. 3 = salt-water disposal." },
  { k: "InjThrough", label: "Injection Through", kind: "select", opts: [["1", "Tubing (1)"], ["2", "Casing (2)"]], help: "Whether injection is through tubing (1) or casing (2)." },
  { k: "InjIntervalDepthTop", label: "Interval Top (ft)", kind: "num", help: "Injection interval TOP depth." },
  { k: "InjIntervalDepthBottom", label: "Interval Bottom (ft)", kind: "num", help: "Injection interval BOTTOM depth (must exceed top)." },
  { k: "TbgPackerDepth", label: "Tubing/Packer Depth (ft)", kind: "num", help: "Tubing/packer setting depth." },
  { k: "SWPct", label: "Salt Water %", kind: "num", help: "Salt-water share. All fluid % must total 100." },
];

const EXCLUSION_REASONS = [
  ["junk_test", "Junk / test lease"],
  ["non_injection", "Not an injection/disposal well"],
  ["duplicate", "Duplicate lease"],
  ["other", "Other"],
];
const reasonLabel = (code) => {
  const found = EXCLUSION_REASONS.find(([v]) => v === code);
  return found ? found[1] : code;
};

// Client-side mirror of server h10_candidate_missing() so a lease's readiness can
// recompute the instant it is completed in the UI — the server re-validates on
// save/execute and remains authoritative. Returns the list of missing/invalid fields.
function rowMissing(r) {
  const m = [];
  const s = (v) => (v == null ? "" : String(v)).trim();
  const pos = (v) => parseInt(s(v), 10) > 0;
  if (s(r.LeaseID) === "") m.push("LeaseID");
  if (s(r.WellNumber) === "") m.push("WellNumber");
  if (!/^(0[1-9]|1[0-2])$/.test(s(r.H10MonthDue))) m.push("H10MonthDue");
  if (!/^\d{8}$/.test(s(r.H10API))) m.push("H10API");
  if (!["1", "2", "3", "4", "6"].includes(s(r.UICType))) m.push("UICType");
  if (!pos(r.InjIntervalDepthTop)) m.push("InjIntervalDepthTop");
  if (!pos(r.InjIntervalDepthBottom)) m.push("InjIntervalDepthBottom");
  if (!pos(r.TbgPackerDepth)) m.push("TbgPackerDepth");
  if (!["1", "2"].includes(s(r.InjThrough))) m.push("InjThrough");
  const sum = FLUID_KEYS.reduce((a, k) => a + (parseFloat(r[k]) || 0), 0);
  if (Math.abs(sum - 100) > 0.01) m.push("FluidPctSum100");
  if ((parseFloat(r.OtherPct) || 0) > 0 && s(r.OtherType1) === "") m.push("OtherType");
  return m;
}
const rowReady = (r) => r.status !== "Finalized" && rowMissing(r).length === 0;

// Effective category for tab/count placement — server category, but a row the
// operator has just completed in the modal promotes from needs_fields → ready live.
function effCat(r) {
  if (r.category === "already_configured") return "already_configured";
  if (r.category === "excluded") return "excluded";
  return rowReady(r) ? "ready" : "needs_fields";
}

// Build the save_draft payload for one lease (whitelisted fields only).
function rowToSaveObj(r) {
  const o = { LeaseID: r.LeaseID, LeaseName: r.LeaseName };
  MODAL_FIELDS.forEach(({ k }) => { o[k] = r[k]; });
  ["NORMPct","FreshPct","TotalCO2Pct","AnthropogenicCO2Pct","NGPct","H2SPct","PolymerPct","SteamPct","AirPct","NitrogenPct","FracWaterFBPct","OtherPct","InjFluidSourceOthers"].forEach((f) => { if (r[f] != null) o[f] = r[f]; });
  return o;
}

const TABS = [
  ["needs_fields", "Needs Fields"],
  ["ready", "Ready"],
  ["excluded", "Excluded"],
  ["already_configured", "Already Configured"],
];

export default function H10ModulePanel({ subdomain, theme = "light", companyName, cycle = "" }) {
  const isDark = theme === "dark";
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [readiness, setReadiness] = useState(null);
  const [initSteps, setInitSteps] = useState(null);
  const [rows, setRows] = useState(null);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState("");
  const [selectedLeases, setSelectedLeases] = useState({}); // {LeaseID:true} chosen for execute
  const [execResult, setExecResult] = useState(null);
  const [tab, setTab] = useState("needs_fields");
  const [actionableOnly, setActionableOnly] = useState(true);
  const [editRow, setEditRow] = useState(null);   // working copy in the Complete Setup modal
  const [excludeRow, setExcludeRow] = useState(null); // {LeaseID, LeaseName} pending exclusion
  const [excludeReason, setExcludeReason] = useState("junk_test");

  const card = isDark ? "bg-gray-800 border-gray-700 text-gray-100" : "bg-white border-gray-200 text-gray-800";
  const sub = isDark ? "border-gray-700" : "border-gray-200";
  const btn = "px-3 py-1.5 rounded text-sm font-medium border transition disabled:opacity-50 disabled:cursor-not-allowed";
  const btnP = isDark ? "bg-blue-600 border-blue-500 text-white hover:bg-blue-500" : "bg-blue-600 border-blue-600 text-white hover:bg-blue-700";
  const btnN = isDark ? "bg-gray-700 border-gray-600 text-gray-100 hover:bg-gray-600" : "bg-gray-100 border-gray-300 text-gray-800 hover:bg-gray-200";
  const inpCls = (bad) => `w-full px-2 py-1 text-sm rounded border ${bad ? "border-red-500 ring-1 ring-red-500" : isDark ? "border-gray-600" : "border-gray-300"} ${isDark ? "bg-gray-900 text-gray-100" : "bg-white"}`;

  const callApi = useCallback(
    async (action, payload = {}) => {
      const tok = sessionStorage.getItem("og_access_token") || "";
      const resp = await fetch(API_BASE, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ action, init_subdomain: subdomain, ...(cycle ? { cycle } : {}), ...payload }),
      });
      const data = await resp.json().catch(() => ({ ok: false, message: `HTTP ${resp.status}` }));
      if (!resp.ok || !data.ok) throw Object.assign(new Error(data.message || `HTTP ${resp.status}`), { data });
      return data;
    },
    [subdomain, cycle]
  );

  const applyReadiness = (resp) => {
    const r = resp?.data?.readiness ?? resp?.readiness;
    if (r) setReadiness(r);
  };

  const doReadiness = useCallback(async () => {
    setBusy("readiness"); setError(""); setNotice("");
    try { applyReadiness(await callApi("h10_readiness")); }
    catch (e) { setError(e.message); applyReadiness(e.data); }
    finally { setBusy(""); }
  }, [callApi]);

  const doInit = useCallback(async (execute) => {
    setBusy(execute ? "init_create" : "init_dry"); setError(""); setNotice(""); setInitSteps(null);
    try {
      const resp = await callApi(execute ? "init_h10_create" : "init_h10_dry_run");
      applyReadiness(resp);
      if (execute) setInitSteps(resp?.steps || resp?.data?.steps || []);
    } catch (e) { setError(e.message); applyReadiness(e.data); }
    finally { setBusy(""); }
  }, [callApi]);

  const doPreview = useCallback(async () => {
    setBusy("preview"); setError("");
    try {
      const resp = await callApi("h10_candidates_preview");
      const res = resp?.data?.result;
      setRows((res?.candidates || []).map((c) => ({ ...c })));
      setSelectedLeases({});
      applyReadiness(resp);
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, [callApi]);

  // Save ONE lease (from the Complete Setup modal). Only that LeaseID is touched.
  const doModalSave = useCallback(async () => {
    if (!editRow) return;
    setBusy("modal_save"); setError("");
    try {
      const resp = await callApi("h10_candidates_save_draft", { candidates: [rowToSaveObj(editRow)] });
      const r0 = resp?.data?.result || {};
      const st = r0.status || (r0.results && r0.results[0] && r0.results[0].status);
      setNotice(`Saved lease ${editRow.LeaseID} — ${st === "ReadyToFinalize" ? "now Ready to execute." : "still needs required fields."}`);
      setEditRow(null);
      applyReadiness(resp);
      await doPreview(); // resync classification (only the saved lease changes category)
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, [editRow, callApi, doPreview]);

  const doSetExclusion = useCallback(async (leaseId, excluded, reason) => {
    setBusy("exclude"); setError(""); setNotice("");
    try {
      await callApi("h10_candidates_set_exclusion", { lease_id: leaseId, excluded, reason: reason || "" });
      setNotice(excluded ? `Excluded ${leaseId} from the H-10 workflow.` : `Restored ${leaseId} into the workflow.`);
      setExcludeRow(null);
      await doPreview();
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, [callApi, doPreview]);

  // ── execute selection (Ready leases) ──────────────────────────────────────
  const readyLeases = (rows || []).filter((r) => effCat(r) === "ready").map((r) => r.LeaseID);
  const selectedReady = readyLeases.filter((lid) => selectedLeases[lid]);
  const execTargets = selectedReady.length ? selectedReady : readyLeases;
  const execCount = execTargets.length;

  const toggleSelect = (lid) => setSelectedLeases((p) => ({ ...p, [lid]: !p[lid] }));

  const doExecute = useCallback(async () => {
    const targets = (rows || []).filter((r) => effCat(r) === "ready" && execTargets.includes(r.LeaseID));
    if (!targets.length) return;
    const n = targets.length;
    if (!window.confirm(
      `This will write ${n} validated H-10 setup row(s) into InjDispWellInfo for this tenant. ` +
      `It will not overwrite existing rows. Continue?`
    )) return;
    const targetSet = new Set(targets.map((t) => t.LeaseID));
    setBusy("execute"); setError(""); setNotice(""); setExecResult(null);
    try {
      await callApi("h10_candidates_save_draft", { candidates: targets.map(rowToSaveObj) });
      const prev = await callApi("h10_candidates_preview");
      const prevCands = prev?.data?.result?.candidates || [];
      const ids = prevCands
        .filter((c) => targetSet.has(c.LeaseID) && c.can_finalize && c.candidate_id)
        .map((c) => c.candidate_id);
      if (!ids.length) {
        setRows(prevCands.map((c) => ({ ...c })));
        applyReadiness(prev);
        setError("Selected lease(s) did not validate server-side — re-check required fields and try again.");
        return;
      }
      const resp = await callApi("h10_candidates_finalize", { candidate_ids: ids });
      const res = resp?.data?.result || null;
      setExecResult(res);
      const executed = res?.executed ?? res?.finalized ?? 0;
      const skipped = res?.skipped ?? 0;
      const errs = (res?.errors || []).length;
      setNotice(`Executed ${executed} row(s) into InjDispWellInfo` + (skipped ? `, ${skipped} skipped` : "") + (errs ? `, ${errs} error(s)` : "") + ".");
      applyReadiness(resp);
      setSelectedLeases({});
      await doPreview();
    } catch (e) {
      setError(e.message);
      setExecResult(e.data?.data?.result || null);
    } finally { setBusy(""); }
  }, [rows, execTargets, callApi, doPreview]);

  const doClear = useCallback(async () => {
    if (!window.confirm("Clear all non-finalized drafts AND exclusions for this tenant? (Configured InjDispWellInfo rows are not affected.)")) return;
    setBusy("clear"); setError(""); setNotice("");
    try {
      const resp = await callApi("h10_candidates_clear_draft");
      setNotice(`Cleared ${resp?.data?.result?.cleared ?? 0} draft(s).`);
      applyReadiness(resp);
      await doPreview();
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, [callApi, doPreview]);

  const copyMsg = useCallback(() => {
    const tm = readiness?.tenant_message; if (!tm) return;
    navigator.clipboard?.writeText(`Subject: ${tm.subject}\n\n${tm.body}`).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2500); });
  }, [readiness]);

  // ── derived: category counts + filtered rows for the active tab ────────────
  const counts = useMemo(() => {
    const c = { ready: 0, needs_fields: 0, excluded: 0, already_configured: 0 };
    (rows || []).forEach((r) => { c[effCat(r)] = (c[effCat(r)] || 0) + 1; });
    return c;
  }, [rows]);

  const visibleTabs = actionableOnly ? TABS.filter(([k]) => k === "needs_fields" || k === "ready") : TABS;
  const activeTab = actionableOnly && (tab === "excluded" || tab === "already_configured") ? "needs_fields" : tab;
  const tabRows = (rows || []).filter((r) => effCat(r) === activeTab);

  const overall = readiness?.overall_status || "—";
  const hint = !subdomain ? "Select a tenant above to begin." : null;

  const editMissing = editRow ? rowMissing(editRow) : [];
  const editFluidTotal = editRow ? FLUID_KEYS.reduce((a, k) => a + (parseFloat(editRow[k]) || 0), 0) : 0;
  const setEdit = (k, v) => setEditRow((p) => ({ ...p, [k]: v }));

  return (
    <div className={`rounded-lg border p-4 mb-4 ${card}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          H-10 Module {companyName ? <span className="text-xs font-normal opacity-70">— {companyName}</span> : null}
          {readiness?.cycle ? <span className="text-xs font-normal opacity-70">· {readiness.cycle}</span> : null}
        </h3>
        <span className="text-xs font-bold px-2 py-1 rounded" style={{ background: LEVEL_COLOR[overall] || LEVEL_COLOR.INFO, color: "#fff" }}>{overall}</span>
      </div>

      {hint ? <div className="text-sm opacity-60">{hint}</div> : null}
      {error ? <div className="text-sm rounded p-2 mb-2" style={{ background: "#c0392b22", color: LEVEL_COLOR.FAIL }}>{error}{/token|unauthor/i.test(error) ? " — admin token may have expired; log out/in." : ""}</div> : null}
      {notice ? <div className="text-sm rounded p-2 mb-2" style={{ background: "#1e8a4922", color: LEVEL_COLOR.PASS }}>{notice}</div> : null}

      {/* Section 1: Module Status */}
      <div className="mb-3">
        <div className="text-xs font-semibold opacity-80 mb-1">1 · Module Status</div>
        <div className="flex flex-wrap gap-2">
          <button className={`${btn} ${btnN}`} disabled={!!busy || !subdomain} onClick={() => doInit(false)}>{busy === "init_dry" ? "…" : "Init Dry Run"}</button>
          <button className={`${btn} ${btnN}`} disabled={!!busy || !subdomain} onClick={() => { if (window.confirm("Run H-10 Init / Repair? Additive & idempotent — creates only missing tables/indexes/triggers.")) doInit(true); }}>{busy === "init_create" ? "…" : "Init / Repair"}</button>
          <button className={`${btn} ${btnP}`} disabled={!!busy || !subdomain} onClick={doReadiness}>{busy === "readiness" ? "…" : "Run Readiness"}</button>
        </div>
        {readiness ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mt-2">
            {Object.keys(SECTION_LABEL).map((k) => {
              const s = readiness.sections?.[k]; const st = s?.status || "PASS"; const n = s?.items?.length || 0;
              return (
                <div key={k} className={`rounded border p-2 ${sub}`}>
                  <div className="text-[11px] font-semibold mb-1">{SECTION_LABEL[k]}</div>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: LEVEL_COLOR[st], color: "#fff" }}>{st}</span>
                  <span className="text-[10px] opacity-70 ml-1">{n}</span>
                </div>
              );
            })}
          </div>
        ) : null}
        {initSteps && initSteps.length ? (
          <details className="text-[11px] mt-1"><summary className="cursor-pointer font-semibold">Init steps ({initSteps.length})</summary>
            <ul className="mt-1 font-mono">{initSteps.map((s, i) => <li key={i}>{s.status} — {s.label}{s.message ? `: ${s.message}` : ""}</li>)}</ul>
          </details>
        ) : null}
      </div>

      {/* Section 2: Missing Setup */}
      {readiness?.missing_items?.length ? (
        <div className="mb-3">
          <div className="text-xs font-semibold opacity-80 mb-1">2 · Missing Setup ({readiness.summary?.fail || 0} FAIL / {readiness.summary?.warn || 0} WARN)</div>
          <div className="overflow-auto" style={{ maxHeight: 200 }}>
            <table className="w-full text-[11px] border-collapse">
              <thead><tr className={isDark ? "text-gray-300" : "text-gray-600"}>{["Sev", "Owner", "Item", "Lease/Well", "Fix"].map((h) => <th key={h} className={`text-left p-1 border-b ${sub}`}>{h}</th>)}</tr></thead>
              <tbody>
                {readiness.missing_items.map((it, i) => (
                  <tr key={i}>
                    <td className="p-1 align-top"><span className="font-bold px-1 rounded" style={{ color: "#fff", background: LEVEL_COLOR[it.severity] }}>{it.severity}</span></td>
                    <td className="p-1 align-top"><span className="font-semibold px-1 rounded" style={{ color: "#fff", background: OWNER_COLOR[it.owner] || OWNER_COLOR.unknown }}>{it.owner}</span></td>
                    <td className="p-1 align-top">{it.message_internal}</td>
                    <td className="p-1 align-top">{it.lease_id ? `${it.lease_id}${it.well_number ? "/" + it.well_number : ""}` : "—"}</td>
                    <td className="p-1 align-top">{it.action_target || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* Section 3: Candidate Setup — classified workflow */}
      {subdomain ? (
        <div className="mb-3">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="text-xs font-semibold opacity-80">3 · Candidate Setup</div>
            <div className="flex gap-2">
              <button className={`${btn} ${btnN}`} disabled={!!busy} onClick={doPreview}>{busy === "preview" ? "…" : rows ? "Refresh" : "Preview Candidates"}</button>
              <button
                className={`${btn} ${btnP}`}
                disabled={!!busy || !readyLeases.length}
                title={!readyLeases.length ? "Complete at least one lease (Complete Setup) to enable execute." : (selectedReady.length ? `Execute ${selectedReady.length} selected` : `Execute all ${readyLeases.length} ready`)}
                onClick={doExecute}
              >
                {busy === "execute" ? "…" : `Execute ${execCount || 0} → InjDispWellInfo`}
              </button>
              {rows ? <button className={`${btn} ${btnN}`} disabled={!!busy} onClick={doClear}>{busy === "clear" ? "…" : "Clear Drafts"}</button> : null}
            </div>
          </div>

          {/* Summary chips */}
          {rows ? (
            <div className="flex flex-wrap items-center gap-2 mb-2 text-[11px]">
              {TABS.map(([k, lbl]) => (
                <span key={k} className="px-2 py-0.5 rounded font-semibold" style={{ background: CAT_COLOR[k], color: "#fff" }}>
                  {lbl}: {counts[k] || 0}
                </span>
              ))}
              <label className="flex items-center gap-1 ml-auto cursor-pointer opacity-80">
                <input type="checkbox" checked={actionableOnly} onChange={(e) => setActionableOnly(e.target.checked)} />
                Show only actionable
              </label>
            </div>
          ) : null}

          {/* Tabs */}
          {rows ? (
            <div className="flex flex-wrap gap-1 mb-2">
              {visibleTabs.map(([k, lbl]) => (
                <button
                  key={k}
                  className={`px-2 py-1 text-[11px] rounded border ${activeTab === k ? "font-bold" : "opacity-70"} ${sub}`}
                  style={activeTab === k ? { background: CAT_COLOR[k], color: "#fff", borderColor: CAT_COLOR[k] } : {}}
                  onClick={() => setTab(k)}
                >
                  {lbl} ({counts[k] || 0})
                </button>
              ))}
            </div>
          ) : null}

          {rows && rows.length ? (
            tabRows.length ? (
              <div className="overflow-auto" style={{ maxHeight: 360 }}>
                <table className="w-full text-[11px] border-collapse">
                  <thead><tr className={isDark ? "text-gray-300" : "text-gray-600"}>
                    {activeTab === "ready" ? <th className={`text-left p-1 border-b ${sub}`}><input type="checkbox" checked={readyLeases.length > 0 && selectedReady.length === readyLeases.length} onChange={(e) => setSelectedLeases(e.target.checked ? Object.fromEntries(readyLeases.map((l) => [l, true])) : {})} /></th> : null}
                    <th className={`text-left p-1 border-b ${sub}`}>Lease</th>
                    <th className={`text-left p-1 border-b ${sub}`}>Well #</th>
                    <th className={`text-left p-1 border-b ${sub}`}>Status</th>
                    <th className={`text-left p-1 border-b ${sub}`}>{activeTab === "excluded" ? "Reason" : activeTab === "already_configured" ? "Due" : "Missing fields"}</th>
                    <th className={`text-left p-1 border-b ${sub}`}>Actions</th>
                  </tr></thead>
                  <tbody>
                    {tabRows.map((r) => {
                      const cat = effCat(r);
                      const miss = rowMissing(r);
                      return (
                        <tr key={r.LeaseID}>
                          {activeTab === "ready" ? (
                            <td className="p-1 align-top text-center">
                              <input type="checkbox" checked={!!selectedLeases[r.LeaseID]} onChange={() => toggleSelect(r.LeaseID)} />
                            </td>
                          ) : null}
                          <td className="p-1 align-top whitespace-nowrap"><span className="font-mono">{r.LeaseID}</span><div className="opacity-60">{r.LeaseName}</div></td>
                          <td className="p-1 align-top">{r.WellNumber || "—"}</td>
                          <td className="p-1 align-top">
                            <span className="px-1 rounded" style={{ color: "#fff", background: CAT_COLOR[cat] }}>
                              {cat === "ready" ? "Ready" : cat === "needs_fields" ? "Needs Fields" : cat === "excluded" ? "Excluded" : "Configured"}
                            </span>
                          </td>
                          <td className="p-1 align-top opacity-80">
                            {cat === "excluded" ? (
                              <span>{r.exclusion_reason || "—"}{r.exclusion_source === "auto" ? <em className="opacity-60"> (auto-suggested)</em> : null}</span>
                            ) : cat === "already_configured" ? (
                              (r.H10MonthDue || "—")
                            ) : (
                              miss.length ? miss.join(", ") : <span style={{ color: LEVEL_COLOR.PASS }}>none ✓</span>
                            )}
                          </td>
                          <td className="p-1 align-top">
                            <div className="flex flex-wrap gap-1">
                              {(cat === "needs_fields" || cat === "ready") ? (
                                <>
                                  <button className={`${btn} ${btnP} !px-2 !py-0.5 !text-[11px]`} disabled={!!busy} onClick={() => setEditRow({ ...r })}>{cat === "ready" ? "Edit" : "Complete Setup"}</button>
                                  <button className={`${btn} ${btnN} !px-2 !py-0.5 !text-[11px]`} disabled={!!busy} onClick={() => { setExcludeRow({ LeaseID: r.LeaseID, LeaseName: r.LeaseName }); setExcludeReason("junk_test"); }}>Exclude</button>
                                </>
                              ) : cat === "excluded" ? (
                                <button className={`${btn} ${btnN} !px-2 !py-0.5 !text-[11px]`} disabled={!!busy} onClick={() => doSetExclusion(r.LeaseID, false, "")}>Restore</button>
                              ) : (
                                <span className="opacity-50">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="text-[10px] opacity-60 mt-1">
                  Fix one lease at a time with <strong>Complete Setup</strong> — enter the real permit values, save, and that lease moves to <strong>Ready</strong> on its own. Then check it and <strong>Execute → InjDispWellInfo</strong>. Suggested defaults (UIC=3, Thru=Tubing, SW=100%) require explicit save. Junk/test &amp; water-supply leases are auto-suggested for exclusion; nothing is ever deleted.
                </div>
              </div>
            ) : (
              <div className="text-[11px] opacity-70 p-2">No leases in “{TABS.find(([k]) => k === activeTab)?.[1]}”.</div>
            )
          ) : rows ? (
            <div className="text-[11px] opacity-70">No INJ candidate leases found (none with WellType='INJ').</div>
          ) : (
            <div className="text-[11px] opacity-60">Click “Preview Candidates” to classify injection-well candidates from existing lease/well data.</div>
          )}

          {/* Execution result */}
          {execResult ? (
            <div className={`rounded border p-2 mt-2 ${sub}`}>
              <div className="text-[11px] font-semibold mb-1">
                Execute result — <span style={{ color: LEVEL_COLOR.PASS }}>{execResult.executed ?? 0} written</span>
                {" · "}<span style={{ color: LEVEL_COLOR.INFO }}>{execResult.skipped ?? 0} skipped</span>
                {" · "}<span style={{ color: LEVEL_COLOR.FAIL }}>{(execResult.errors || []).length} error(s)</span>
                {" "}<span className="opacity-60">(of {execResult.attempted ?? 0} attempted)</span>
              </div>
              {(execResult.rows || []).length ? (
                <div className="overflow-auto" style={{ maxHeight: 180 }}>
                  <table className="w-full text-[11px] border-collapse">
                    <thead><tr className={isDark ? "text-gray-300" : "text-gray-600"}>{["Lease / Well", "Result", "Detail"].map((h) => <th key={h} className={`text-left p-1 border-b ${sub}`}>{h}</th>)}</tr></thead>
                    <tbody>
                      {execResult.rows.map((rr, i) => {
                        const c = rr.result === "executed" ? LEVEL_COLOR.PASS : rr.result === "skipped" ? LEVEL_COLOR.INFO : LEVEL_COLOR.FAIL;
                        return (
                          <tr key={i}>
                            <td className="p-1 align-top whitespace-nowrap font-mono">{rr.lease_id || "—"}{rr.well_number ? `/${rr.well_number}` : ""}</td>
                            <td className="p-1 align-top"><span className="px-1 rounded font-semibold" style={{ color: "#fff", background: c }}>{rr.result}</span></td>
                            <td className="p-1 align-top opacity-80">{rr.reason}{(rr.missing_required || []).length ? ` — ${rr.missing_required.join(", ")}` : ""}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {(execResult.next_steps || []).length ? (
                <ul className="text-[10px] opacity-70 mt-1 list-disc ml-4">{execResult.next_steps.map((s, i) => <li key={i}>{s}</li>)}</ul>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Section 4: Tenant Message */}
      {readiness?.tenant_message ? (
        <div className={`rounded border p-2 ${sub}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="text-xs font-semibold opacity-80">4 · Tenant Setup Request</div>
            <button className={`${btn} ${btnP} !py-0.5`} onClick={copyMsg}>{copied ? "Copied ✓" : "Copy"}</button>
          </div>
          <div className="text-[11px] font-semibold">Subject: {readiness.tenant_message.subject}</div>
          <pre className="text-[11px] whitespace-pre-wrap mt-1 max-h-48 overflow-auto">{readiness.tenant_message.body}</pre>
        </div>
      ) : null}

      {/* Complete Setup modal — focused per-lease editor */}
      {editRow ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => !busy && setEditRow(null)}>
          <div className={`w-full max-w-2xl max-h-[90vh] overflow-auto rounded-lg border p-4 ${card}`} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-base font-semibold">Complete H-10 Setup</h4>
              <button className={`${btn} ${btnN} !py-0.5`} disabled={!!busy} onClick={() => setEditRow(null)}>Close</button>
            </div>
            <div className="text-[12px] mb-2">
              <span className="font-mono">{editRow.LeaseID}</span> <span className="opacity-60">{editRow.LeaseName}</span>
              <span className="ml-2 px-1.5 py-0.5 rounded text-[11px] font-semibold" style={{ color: "#fff", background: editMissing.length ? LEVEL_COLOR.WARN : LEVEL_COLOR.PASS }}>
                {editMissing.length ? `${editMissing.length} field(s) missing` : "Ready ✓"}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {MODAL_FIELDS.map((f) => {
                const bad = editMissing.includes(f.k) || (f.k === "SWPct" && editMissing.includes("FluidPctSum100"));
                return (
                  <div key={f.k}>
                    <label className="text-[11px] font-semibold flex items-center gap-1">
                      {f.label}{bad ? <span style={{ color: LEVEL_COLOR.FAIL }}>*</span> : null}
                    </label>
                    {f.kind === "select" ? (
                      <select className={inpCls(bad)} value={editRow[f.k] ?? ""} onChange={(e) => setEdit(f.k, e.target.value)}>
                        <option value="">— select —</option>
                        {f.opts.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
                      </select>
                    ) : (
                      <input className={inpCls(bad)} inputMode={f.kind === "num" ? "numeric" : undefined} placeholder={f.ph || ""} value={editRow[f.k] ?? ""} onChange={(e) => setEdit(f.k, e.target.value)} />
                    )}
                    <div className="text-[10px] opacity-60 mt-0.5">{f.help}</div>
                  </div>
                );
              })}
            </div>

            {/* Advanced fluid composition */}
            <details className="mt-3">
              <summary className="cursor-pointer text-[11px] font-semibold">
                Advanced fluid composition — total {editFluidTotal}%{" "}
                <span style={{ color: Math.abs(editFluidTotal - 100) < 0.01 ? LEVEL_COLOR.PASS : LEVEL_COLOR.FAIL }}>
                  {Math.abs(editFluidTotal - 100) < 0.01 ? "✓" : "(must total 100)"}
                </span>
              </summary>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                {EXTRA_FLUID_KEYS.map((k) => (
                  <div key={k}>
                    <label className="text-[10px] font-semibold">{k.replace("Pct", " %")}</label>
                    <input className={inpCls(false)} inputMode="numeric" value={editRow[k] ?? 0} onChange={(e) => setEdit(k, e.target.value)} />
                  </div>
                ))}
              </div>
            </details>

            <div className="flex items-center justify-end gap-2 mt-4">
              <button className={`${btn} ${btnN}`} disabled={!!busy} onClick={() => setEditRow(null)}>Cancel</button>
              <button className={`${btn} ${btnP}`} disabled={!!busy} onClick={doModalSave}>
                {busy === "modal_save" ? "…" : editMissing.length ? "Save Progress" : "Save — Mark Ready"}
              </button>
            </div>
            <div className="text-[10px] opacity-60 mt-2">Saving writes a draft for this lease only (never InjDispWellInfo). When all required fields are valid the lease becomes Ready; you then execute it from the Ready tab. No value is written as a regulatory confirmation until you Execute.</div>
          </div>
        </div>
      ) : null}

      {/* Exclude reason modal */}
      {excludeRow ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={() => !busy && setExcludeRow(null)}>
          <div className={`w-full max-w-sm rounded-lg border p-4 ${card}`} onClick={(e) => e.stopPropagation()}>
            <h4 className="text-base font-semibold mb-1">Exclude lease from H-10 workflow</h4>
            <div className="text-[12px] mb-2"><span className="font-mono">{excludeRow.LeaseID}</span> <span className="opacity-60">{excludeRow.LeaseName}</span></div>
            <label className="text-[11px] font-semibold">Reason</label>
            <select className={inpCls(false)} value={excludeReason} onChange={(e) => setExcludeReason(e.target.value)}>
              {EXCLUSION_REASONS.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
            </select>
            <div className="text-[10px] opacity-60 mt-1">Excluding only moves this lease out of the action queue (auditable, restorable). It never deletes the lease or any InjDispWellInfo row.</div>
            <div className="flex items-center justify-end gap-2 mt-3">
              <button className={`${btn} ${btnN}`} disabled={!!busy} onClick={() => setExcludeRow(null)}>Cancel</button>
              <button className={`${btn} ${btnP}`} disabled={!!busy} onClick={() => doSetExclusion(excludeRow.LeaseID, true, reasonLabel(excludeReason))}>{busy === "exclude" ? "…" : "Exclude"}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
